import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Brain, Search, Sparkles } from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  ConfidenceBar,
  Input,
  KeyValueGrid,
  MetricCard,
  Tabs,
  type TabItem,
} from '@/components';
import { buildKnowledgeGraph, JDL_LEVELS } from '@/domain';
import { useData, useUI } from '@/data/stores';
import { fmtRelative, fmtUTC } from '@/lib/format';
import { demoTimestamp, downloadJson } from '@/lib/export';
import type { KGEntity } from '@/types';
import './intel.css';

const SUGGESTED = [
  'Vessels near AST-XX-ESTL2-CBL',
  'Alerts for AST-EE-VIRU-SUB',
  'What does Sandworm target',
  'Path between AST-NL-MAGN-01 and AST-DE-FRAN-DC1',
  'Assets in EE',
];

export function KnowledgeGraphView() {
  const data = useData();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const notify = useUI((s) => s.notifyAction);
  const queryParam = params.get('q') ?? '';

  const kg = useMemo(
    () =>
      buildKnowledgeGraph({
        assets: data.assets,
        alerts: data.alerts,
        vessels: data.vessels,
        reports: data.reports,
        actors: data.actors,
        vulnerabilities: data.vulnerabilities,
        edges: data.edges,
      }),
    [data],
  );

  const [q, setQ] = useState('');
  const [submitted, setSubmitted] = useState<string>('');
  const [selected, setSelected] = useState<KGEntity | null>(null);

  useEffect(() => {
    if (!queryParam) return;
    setQ(queryParam);
    setSubmitted(queryParam);
    setSelected(null);
  }, [queryParam]);

  const result = useMemo(() => {
    if (!submitted) return null;
    return kg.query(submitted);
  }, [kg, submitted]);

  const totals = useMemo(() => {
    const all = kg.all();
    const counts: Record<string, number> = {};
    for (const e of all) counts[e.kind] = (counts[e.kind] ?? 0) + 1;
    return counts;
  }, [kg]);

  const tabs: TabItem[] = [
    {
      key: 'query',
      label: 'Query',
      content: (
        <div className="iris-intel-grid">
          <Card title="Natural-language query" subtitle="Ask about entities, paths, attribution, observations">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                setSubmitted(q);
              }}
              className="iris-kg-query"
            >
              <Input
                placeholder='e.g. "vessels near AST-XX-ESTL2-CBL"'
                value={q}
                onChange={(e) => setQ(e.target.value)}
                startIcon={<Search size={14} strokeWidth={1.5} />}
              />
              <Button
                variant="primary"
                icon={<Sparkles size={14} strokeWidth={1.5} />}
                type="submit"
              >
                Query
              </Button>
            </form>
            <div className="iris-kg-suggestions">
              <span className="caption">Try:</span>
              {SUGGESTED.map((s) => (
                <button
                  key={s}
                  className="iris-kg-suggestion"
                  onClick={() => {
                    setQ(s);
                    setSubmitted(s);
                  }}
                >
                  {s}
                </button>
              ))}
            </div>
          </Card>

          {result && (
            <Card title="Results" subtitle={result.summary}>
              {result.entities.length === 0 ? (
                <div className="caption">No entities matched.</div>
              ) : (
                <ul className="iris-kg-results">
                  {result.entities.slice(0, 25).map((e) => (
                    <li
                      key={e.entity_id}
                      onClick={() => {
                        setSelected(e);
                        if (e.kind === 'asset') navigate(`/assets/${e.entity_id}`);
                      }}
                      className="iris-kg-result-row"
                    >
                      <Badge color={kindColor(e.kind)}>{e.kind}</Badge>
                      <div className="iris-kg-result-name">{e.name}</div>
                      <span className="caption mono">{e.entity_id}</span>
                      <ConfidenceBar value={e.confidence} />
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          )}

          {selected && (
            <Card title={`Entity · ${selected.name}`} subtitle={selected.kind}>
              <KeyValueGrid
                cols={2}
                items={[
                  { label: 'ID', value: selected.entity_id, mono: true },
                  { label: 'Confidence', value: <ConfidenceBar value={selected.confidence} /> },
                  { label: 'Sources', value: selected.sources.join(', ') || '-' },
                  ...(selected.aliases?.length
                    ? [{ label: 'Aliases', value: selected.aliases.join(', ') }]
                    : []),
                  ...Object.entries(selected.attributes).map(([k, v]) => ({
                    label: k,
                    value: String(v),
                    mono: true,
                  })),
                ]}
              />
            </Card>
          )}
        </div>
      ),
    },
    {
      key: 'feed',
      label: 'Intelligence feed',
      badge: <span>{data.reports.length}</span>,
      content: <IntelFeed />,
    },
    {
      key: 'actors',
      label: 'Threat actors',
      badge: <span>{data.actors.length}</span>,
      content: <ActorsPanel />,
    },
    {
      key: 'fusion',
      label: 'JDL fusion status',
      content: (
        <div className="iris-intel-grid">
          {JDL_LEVELS.map((l) => (
            <Card
              key={l.level}
              title={l.label}
              subtitle={l.description}
              accent={
                l.health === 'green'
                  ? 'var(--status-operational)'
                  : l.health === 'amber'
                    ? 'var(--status-degraded)'
                    : 'var(--status-critical)'
              }
            >
              <KeyValueGrid
                cols={2}
                items={[
                  { label: 'Throughput', value: `${l.throughput_per_min}/min`, mono: true },
                  { label: 'Activity', value: l.current_activity },
                ]}
              />
            </Card>
          ))}
        </div>
      ),
    },
  ];

  return (
    <div className="iris-page">
      <header className="iris-page-header">
        <div>
          <h1>Intelligence & Knowledge Graph</h1>
          <p className="caption">
            Layer 2 - Multi-source fusion (JDL Levels 0-4) into a queryable graph of assets,
            vessels, threat actors, alerts, and reports with full source attribution.
          </p>
        </div>
        <div className="iris-page-actions">
          <Button
            icon={<Brain size={14} strokeWidth={1.5} />}
            onClick={() => {
              downloadJson(`iris-kg-schema-${demoTimestamp()}.json`, {
                generated_at: new Date().toISOString(),
                node_kinds: ['asset', 'vessel', 'event', 'document', 'threat_actor', 'vulnerability'],
                edge_kinds: [
                  'feeds',
                  'depends_on',
                  'controls',
                  'communicates_with',
                  'shares_corridor',
                  'supplies',
                  'backs_up',
                  'attributed_to',
                  'mentions',
                ],
                totals,
              });
              notify({
                kind: 'success',
                title: 'KG schema exported',
                body: 'Knowledge-graph schema and entity totals downloaded as JSON.',
              });
            }}
          >
            Open KG schema
          </Button>
        </div>
      </header>

      <section className="iris-page-metrics">
        <MetricCard label="Assets" value={totals['asset'] ?? 0} />
        <MetricCard label="Vessels" value={totals['vessel'] ?? 0} />
        <MetricCard label="Events" value={totals['event'] ?? 0} status="var(--status-degraded)" />
        <MetricCard label="Reports" value={totals['document'] ?? 0} />
        <MetricCard label="Threat actors" value={totals['threat_actor'] ?? 0} status="var(--status-critical)" />
        <MetricCard label="Total entities" value={Object.values(totals).reduce((a, b) => a + b, 0)} />
      </section>

      <Tabs items={tabs} />
    </div>
  );
}

function IntelFeed() {
  const reports = useData((s) => s.reports);
  return (
    <div className="iris-intel-feed">
      {reports.map((r) => (
        <Card
          key={r.report_id}
          title={r.title}
          subtitle={`${r.source} · ${r.classification.toUpperCase()} · ${fmtRelative(r.timestamp)}`}
        >
          <p>{r.body}</p>
          <KeyValueGrid
            cols={3}
            items={[
              {
                label: 'Reliability',
                value: r.confidence.source_reliability,
                mono: true,
              },
              {
                label: 'Accuracy',
                value: r.confidence.information_accuracy,
                mono: true,
              },
              {
                label: 'Composite',
                value: <ConfidenceBar value={r.confidence.composite} />,
              },
              {
                label: 'Corroboration',
                value: `${r.confidence.corroboration_count} sources`,
                mono: true,
              },
              {
                label: 'Linked assets',
                value: r.related_asset_ids.length,
                mono: true,
              },
              { label: 'Issued', value: fmtUTC(r.timestamp), mono: true },
            ]}
          />
        </Card>
      ))}
    </div>
  );
}

function ActorsPanel() {
  const actors = useData((s) => s.actors);
  return (
    <div className="iris-intel-grid">
      {actors.map((a) => (
        <Card
          key={a.actor_id}
          title={a.name}
          subtitle={a.aliases.join(' · ')}
          accent={
            a.threat_level === 5
              ? 'var(--status-critical)'
              : a.threat_level === 4
                ? 'var(--status-degraded)'
                : 'var(--status-info)'
          }
        >
          <KeyValueGrid
            cols={2}
            items={[
              { label: 'Actor ID', value: a.actor_id, mono: true },
              { label: 'Threat level', value: `${a.threat_level} / 5`, mono: true },
              { label: 'Attribution', value: a.attribution },
              { label: 'Active since', value: a.active_since, mono: true },
              { label: 'Motivation', value: a.motivation.join(', ') },
              { label: 'Target sectors', value: a.target_sectors.join(', ') },
              { label: 'Target regions', value: a.target_regions.join(', ') },
              { label: 'Known malware', value: a.known_malware.join(', ') },
              { label: 'TTPs', value: a.ttps.join(', '), mono: true },
            ]}
          />
        </Card>
      ))}
    </div>
  );
}

function kindColor(kind: KGEntity['kind']): string {
  switch (kind) {
    case 'asset':
      return 'var(--status-info)';
    case 'vessel':
      return 'var(--status-degraded)';
    case 'threat_actor':
      return 'var(--status-critical)';
    case 'event':
      return 'var(--sev-4)';
    case 'document':
      return 'var(--text-secondary)';
    default:
      return 'var(--text-secondary)';
  }
}
